import React, { useEffect, useState, useRef } from "react";
import { api } from "../../lib/api";
import { useTreatments } from "../../lib/treatments";
import { resolveImage } from "../../lib/media";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Plus, PencilSimple, Trash, UploadSimple, EyeSlash, Eye } from "@phosphor-icons/react";

function TreatmentDialog({ trigger, initial, onSaved }) {
  const editing = Boolean(initial?.id);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ title: "", short: "", image: "", order: 100, published: true, title_hi: "", short_hi: "" });

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        title: initial.title || "", short: initial.short || "",
        image: initial.image || "", order: initial.order ?? 100,
        published: initial.published ?? true,
        title_hi: initial.title_hi || "", short_hi: initial.short_hi || "",
      } : { title: "", short: "", image: "", order: 100, published: true, title_hi: "", short_hi: "" });
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, image: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.short) { toast.error("Title and short description are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 100 };
      if (editing) {
        await api.patch(`/treatments/${initial.id}`, payload);
        toast.success("Treatment updated");
      } else {
        await api.post("/treatments", payload);
        toast.success("Treatment added");
      }
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const preview = resolveImage(form.image);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl bg-cream max-h-[90vh] overflow-y-auto" data-testid="treatment-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-2xl">{editing ? "Edit treatment" : "Add a new treatment"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="treatment-form">
          <div><Label>Title *</Label><Input data-testid="tr-title" value={form.title} onChange={set("title")} className="bg-ivory h-11 mt-1" required placeholder="e.g., Ayurvedic Detox"/></div>
          <div><Label>Short description *</Label><Textarea data-testid="tr-short" value={form.short} onChange={set("short")} className="bg-ivory mt-1 min-h-[80px]" required placeholder="One or two lines shown on the Home page bento and Treatments page card."/></div>

          <div className="bg-sand/40 border border-border/40 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-[0.22em] text-forest font-medium">हिंदी</span>
              <span className="text-xs text-muted-foreground">— optional. Shown when site is in Hindi. Falls back to English if blank.</span>
            </div>
            <div><Label>शीर्षक (Title in Hindi)</Label><Input data-testid="tr-title-hi" value={form.title_hi} onChange={set("title_hi")} className="bg-ivory h-11 mt-1" placeholder="e.g., पंचकर्म डिटॉक्स"/></div>
            <div><Label>विवरण (Short description in Hindi)</Label><Textarea data-testid="tr-short-hi" value={form.short_hi} onChange={set("short_hi")} className="bg-ivory mt-1 min-h-[70px]" placeholder="होम पेज और उपचार पेज पर दिखाए जाने वाली एक-दो पंक्तियाँ।"/></div>
          </div>

          <div>
            <Label>Image</Label>
            <div className="mt-1 flex flex-col md:flex-row gap-3 items-start">
              <div className="w-28 h-28 rounded-xl overflow-hidden bg-ivory border border-border/40 shrink-0 flex items-center justify-center">
                {preview ? <img src={preview} alt="preview" className="w-full h-full object-cover"/> : <span className="text-[10px] text-muted-foreground text-center px-2">No image</span>}
              </div>
              <div className="flex-1 w-full">
                <Input data-testid="tr-image" value={form.image} onChange={set("image")} className="bg-ivory h-11" placeholder="Paste a URL or upload"/>
                <input type="file" accept="image/*" ref={fileRef} onChange={onFilePick} className="hidden"/>
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="tr-upload" className="mt-2 inline-flex items-center gap-2 rounded-full border border-border px-4 py-1.5 text-xs text-forest hover:bg-forest hover:text-cream transition-all disabled:opacity-50">
                  <UploadSimple size={14}/> {uploading ? "Uploading…" : "Upload from computer"}
                </button>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Display order</Label>
              <Input data-testid="tr-order" type="number" value={form.order} onChange={set("order")} className="bg-ivory h-11 mt-1" placeholder="Lower = shown first"/>
              <p className="text-xs text-muted-foreground mt-1">Lower number appears first on the site.</p>
            </div>
            <div>
              <Label>Visibility</Label>
              <label className="mt-2 flex items-center gap-3 bg-ivory border border-border/50 rounded-xl px-4 h-11 cursor-pointer">
                <input type="checkbox" checked={form.published} onChange={set("published")} data-testid="tr-published" className="w-4 h-4 accent-forest"/>
                <span className="text-sm">{form.published ? "Published on site" : "Hidden (draft)"}</span>
              </label>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-border px-5 py-2 text-sm">Cancel</button>
            <button type="submit" disabled={saving || uploading} data-testid="tr-submit" className="rounded-full bg-forest text-cream px-6 py-2.5 text-sm hover:bg-terracotta transition-all disabled:opacity-50">
              {saving ? "Saving…" : (editing ? "Save changes" : "Add treatment")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTreatments() {
  const { refresh } = useTreatments();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/treatments", { params: { all: true } });
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSaved = async () => { await load(); refresh(); };

  const togglePublished = async (t) => {
    try {
      await api.patch(`/treatments/${t.id}`, { title: t.title, short: t.short, image: t.image, order: t.order, published: !t.published });
      toast.success(!t.published ? "Published" : "Hidden");
      onSaved();
    } catch { toast.error("Failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/treatments/${t.id}`); toast.success("Deleted"); onSaved(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-treatments">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <h1 className="font-serif-display text-4xl">Treatments</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Manage the specialities shown on the Home bento grid, Treatments page, and the consultation booking form.</p>
        </div>
        <TreatmentDialog
          onSaved={onSaved}
          trigger={
            <button data-testid="add-treatment-btn" className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-5 py-2.5 text-sm hover:bg-terracotta transition-all">
              <Plus size={16} weight="bold"/> Add treatment
            </button>
          }
        />
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading && <div className="text-muted-foreground">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="col-span-full text-center text-muted-foreground py-16">No treatments yet. Click "Add treatment" to create your first one.</div>
        )}
        {rows.map(t => {
          const preview = resolveImage(t.image);
          return (
            <div key={t.id} className="bg-ivory border border-border/50 rounded-2xl p-5 flex gap-5" data-testid={`treatment-row-${t.id}`}>
              <div className="w-28 h-28 rounded-xl overflow-hidden bg-cream border border-border/40 shrink-0 flex items-center justify-center">
                {preview ? <img src={preview} alt={t.title} className="w-full h-full object-cover"/> : <span className="text-[10px] text-muted-foreground">No image</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2 flex-wrap">
                  <h3 className="font-serif-display text-xl leading-tight">{t.title}</h3>
                  {!t.published && <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] bg-sand text-muted-foreground">Draft</span>}
                  <span className="text-xs text-muted-foreground ml-auto">#{t.order}</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{t.short}</p>
                <div className="mt-3 flex items-center gap-4 text-xs">
                  <TreatmentDialog
                    initial={t}
                    onSaved={onSaved}
                    trigger={
                      <button className="text-forest hover:text-terracotta inline-flex items-center gap-1" data-testid={`edit-treatment-${t.id}`}>
                        <PencilSimple size={14}/> Edit
                      </button>
                    }
                  />
                  <button onClick={() => togglePublished(t)} data-testid={`toggle-${t.id}`} className="text-forest hover:text-terracotta inline-flex items-center gap-1">
                    {t.published ? <><EyeSlash size={14}/> Hide</> : <><Eye size={14}/> Publish</>}
                  </button>
                  <button onClick={() => remove(t)} data-testid={`delete-treatment-${t.id}`} className="text-destructive hover:underline inline-flex items-center gap-1">
                    <Trash size={14}/> Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
